import { useState, useMemo } from 'react';

const fmt = (n: number) => `${Math.round(n).toLocaleString('pl-PL')} zł`;
const pctFmt = (n: number) => `${n.toFixed(2).replace('.', ',')}%`;

function pmtBalloon(PV: number, FV: number, r: number, n: number): number {
  const m = r / 1200;
  if (m === 0) return (PV - FV) / n;
  return (PV - FV / Math.pow(1 + m, n)) * m / (1 - Math.pow(1 + m, -n));
}

type TaxRate = 0.12 | 0.19 | 0.32 | 0.09;

interface SliderFieldProps {
  label: string;
  value: number;
  display: React.ReactNode;
  min: number;
  max: number;
  step: number;
  marks: [string, string, string];
  onChange: (v: number) => void;
}

function SliderField({ label, value, display, min, max, step, marks, onChange }: SliderFieldProps) {
  return (
    <div className="mb-5">
      <div className="flex justify-between items-baseline mb-2">
        <span className="text-[11px] font-bold text-foreground-500 uppercase tracking-wider">{label}</span>
        <span className="text-sm font-extrabold text-foreground-950 tracking-tight">{display}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full cursor-pointer"
      />
      <div className="flex justify-between text-[10px] font-semibold text-foreground-400 mt-1">
        <span>{marks[0]}</span>
        <span>{marks[1]}</span>
        <span>{marks[2]}</span>
      </div>
    </div>
  );
}

export default function LeasingVsCreditCalculator() {
  const [val, setVal] = useState(150000);
  const [downPct, setDownPct] = useState(10);
  const [months, setMonths] = useState(48);
  const [buyPct, setBuyPct] = useState(1);
  const [rateL, setRateL] = useState(8.5);
  const [rateK, setRateK] = useState(7.8);
  const [taxRate, setTaxRate] = useState<TaxRate>(0.19);

  const taxOptions: { label: string; value: TaxRate }[] = [
    { label: 'Skala 12%', value: 0.12 },
    { label: 'Liniowy 19%', value: 0.19 },
    { label: 'Skala 32%', value: 0.32 },
    { label: 'CIT 9%', value: 0.09 },
  ];

  const { lRata, lSum, lShield, lNet, kRata, kSum, kShield, kNet, diff, lWins } = useMemo(() => {
    const down = val * downPct / 100;
    const buy = val * buyPct / 100;

    const lRata = pmtBalloon(val - down, buy, rateL, months);
    const lSum = down + lRata * months + buy;
    const lShield = lSum * taxRate;
    const lNet = lSum - lShield;

    const kRata = pmtBalloon(val - down, 0, rateK, months);
    const kSum = down + kRata * months;
    const kInterest = kRata * months - (val - down);
    const kShield = (kInterest + val) * taxRate;
    const kNet = kSum - kShield;

    const diff = kNet - lNet;
    const lWins = diff > 0;

    return { lRata, lSum, lShield, lNet, kRata, kSum, kShield, kNet, diff, lWins };
  }, [val, downPct, months, buyPct, rateL, rateK, taxRate]);

  const hintText = useMemo(() => {
    if (Math.abs(diff) < val * 0.02) {
      return (
        <>
          Różnica jest niewielka — o wyborze powinny zdecydować szczegóły: VAT, limit 150 tys. dla aut osobowych, elastyczność spłaty i wpływ na zdolność kredytową firmy. To dokładnie te niuanse, które ekspert kredytowy rozstrzygnie na Twoich liczbach.
        </>
      );
    }
    if (lWins) {
      return (
        <>
          Przy tych parametrach wygrywa leasing — głównie dzięki temu, że <b>cała opłata jest kosztem</b>. Pamiętaj jednak o limitach dla aut osobowych i rozliczeniu VAT. Ekspert kredytowy zbierze konkretne oferty leasingowe i zweryfikuje wynik.
        </>
      );
    }
    return (
      <>
        Przy tych parametrach wygrywa kredyt — niższe oprocentowanie i amortyzacja przeważają nad tarczą leasingu. Warto sprawdzić realne marże kredytów firmowych — ekspert kredytowy porówna oferty banków bezpłatnie.
      </>
    );
  }, [diff, val, lWins]);

  return (
    <div>
      {/* Hero */}
      <section className="bg-foreground-950 text-white py-14 md:py-20">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-primary-400 mb-3 opacity-0 animate-slide-up animation-delay-100">
            Dla firm · Leasing vs kredyt
          </div>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white mx-auto max-w-2xl text-center leading-tight mb-4 opacity-0 animate-slide-up animation-delay-200">
            Leasing czy kredyt? Policz, co się bardziej opłaca Twojej firmie
          </h1>
          <p className="text-sm md:text-base text-white/60 mx-auto max-w-xl text-center leading-relaxed opacity-0 animate-slide-up animation-delay-300">
            Porównanie uwzględnia to, co najważniejsze, a co pomija większość kalkulatorów: tarczę podatkową — czyli ile realnie odzyskasz w podatku dzięki kosztom uzyskania przychodu przy każdej z form finansowania.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
            {/* Inputs */}
            <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6 shadow-sm">
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-5">
                Parametry finansowania
              </h3>

              <SliderField
                label="Wartość netto przedmiotu"
                value={val}
                display={<>{val.toLocaleString('pl-PL')} <span className="text-foreground-400 font-semibold text-xs">zł</span></>}
                min={20000}
                max={1000000}
                step={5000}
                marks={['20 tys.', '500 tys.', '1 mln']}
                onChange={setVal}
              />

              <SliderField
                label="Wpłata własna / opłata wstępna"
                value={downPct}
                display={<>{downPct}<span className="text-foreground-400 font-semibold text-xs">%</span></>}
                min={0}
                max={45}
                step={5}
                marks={['0%', '20%', '45%']}
                onChange={setDownPct}
              />

              <SliderField
                label="Okres finansowania"
                value={months}
                display={<>{months} <span className="text-foreground-400 font-semibold text-xs">mies.</span></>}
                min={24}
                max={84}
                step={12}
                marks={['24', '48', '84']}
                onChange={setMonths}
              />

              <SliderField
                label="Wykup w leasingu"
                value={buyPct}
                display={<>{buyPct}<span className="text-foreground-400 font-semibold text-xs">%</span></>}
                min={1}
                max={40}
                step={1}
                marks={['1%', '20%', '40%']}
                onChange={setBuyPct}
              />

              <SliderField
                label="Oprocentowanie leasingu"
                value={rateL}
                display={<>{pctFmt(rateL)}</>}
                min={4}
                max={15}
                step={0.1}
                marks={['4%', '9,5%', '15%']}
                onChange={setRateL}
              />

              <SliderField
                label="Oprocentowanie kredytu"
                value={rateK}
                display={<>{pctFmt(rateK)}</>}
                min={4}
                max={15}
                step={0.1}
                marks={['4%', '9,5%', '15%']}
                onChange={setRateK}
              />

              <div className="mb-0">
                <span className="text-[11px] font-bold text-foreground-500 uppercase tracking-wider block mb-2">
                  Forma opodatkowania
                </span>
                <div className="grid grid-cols-4 border-[1.5px] border-background-200 rounded-xl overflow-hidden">
                  {taxOptions.map((opt) => (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => setTaxRate(opt.value)}
                      className={`py-2.5 text-xs font-bold transition-colors cursor-pointer whitespace-nowrap ${taxRate === opt.value ? 'bg-primary-500 text-white' : 'bg-white text-foreground-500 hover:bg-background-100'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6 shadow-sm">
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-5">
                Wynik porównania
              </h3>

              <div className="bg-green-800 rounded-xl text-white p-6 text-center">
                <div className="text-[10px] font-bold tracking-[0.15em] uppercase text-green-300">
                  Korzystniejszy wariant
                </div>
                <div className="text-xl md:text-2xl font-extrabold tracking-tight mt-1">
                  {lWins ? 'Leasing operacyjny' : 'Kredyt firmowy'}
                </div>
                <div className="text-sm text-green-200 mt-1">
                  tańszy o {fmt(Math.abs(diff))} po uwzględnieniu podatku
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className={`border rounded-xl p-4 ${lWins ? 'border-green-200 bg-green-50' : 'border-background-200'}`}>
                  <h4 className="text-[11px] font-bold uppercase tracking-wider text-foreground-500 mb-2 flex justify-between items-center">
                    Leasing
                    {lWins && <span className="text-[10px] bg-green-600 text-white rounded-full px-2 py-0.5">taniej</span>}
                  </h4>
                  <div className="flex justify-between py-1.5 border-b border-background-100 text-xs">
                    <span className="text-foreground-500">Rata miesięczna</span>
                    <b className="text-foreground-900 whitespace-nowrap">{fmt(lRata)}</b>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-background-100 text-xs">
                    <span className="text-foreground-500">Suma opłat</span>
                    <b className="text-foreground-900 whitespace-nowrap">{fmt(lSum)}</b>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-background-100 text-xs">
                    <span className="text-foreground-500">Tarcza podatkowa</span>
                    <b className="text-green-600 whitespace-nowrap">−{fmt(lShield)}</b>
                  </div>
                  <div className="text-lg font-extrabold tracking-tight text-foreground-950 mt-2">
                    {fmt(lNet)}
                  </div>
                  <div className="text-[10px] text-foreground-400 font-semibold">koszt po podatku</div>
                </div>

                <div className={`border rounded-xl p-4 ${!lWins ? 'border-green-200 bg-green-50' : 'border-background-200'}`}>
                  <h4 className="text-[11px] font-bold uppercase tracking-wider text-foreground-500 mb-2 flex justify-between items-center">
                    Kredyt
                    {!lWins && <span className="text-[10px] bg-green-600 text-white rounded-full px-2 py-0.5">taniej</span>}
                  </h4>
                  <div className="flex justify-between py-1.5 border-b border-background-100 text-xs">
                    <span className="text-foreground-500">Rata miesięczna</span>
                    <b className="text-foreground-900 whitespace-nowrap">{fmt(kRata)}</b>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-background-100 text-xs">
                    <span className="text-foreground-500">Suma spłat</span>
                    <b className="text-foreground-900 whitespace-nowrap">{fmt(kSum)}</b>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-background-100 text-xs">
                    <span className="text-foreground-500">Tarcza podatkowa</span>
                    <b className="text-green-600 whitespace-nowrap">−{fmt(kShield)}</b>
                  </div>
                  <div className="text-lg font-extrabold tracking-tight text-foreground-950 mt-2">
                    {fmt(kNet)}
                  </div>
                  <div className="text-[10px] text-foreground-400 font-semibold">koszt po podatku</div>
                </div>
              </div>

              <div className="bg-primary-100 border border-primary-200 rounded-xl p-4 mt-4 text-sm text-primary-800 leading-relaxed">
                {hintText}
              </div>

              <p className="text-[11px] text-foreground-400 leading-relaxed mt-4">
                Model uproszczony: leasing operacyjny (opłata wstępna, raty i wykup w kosztach uzyskania przychodu), kredyt — w kosztach odsetki oraz pełna amortyzacja przedmiotu. Kwoty netto, bez rozliczenia VAT. Dla samochodów osobowych obowiązuje limit 150 000 zł kosztów oraz ograniczenia odliczenia VAT — wynik może się różnić. Ostateczną opłacalność warto potwierdzić z doradcą i księgowym.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="bg-foreground-950 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute -right-20 -top-20 w-60 h-60 rounded-full bg-primary-500/10 blur-3xl pointer-events-none" />
            <div className="relative z-10">
              <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-white mb-3">
                Ekspert kredytowy zbierze dla Ciebie realne oferty leasingu i kredytu firmowego
              </h2>
              <p className="text-sm text-white/60 max-w-xl mx-auto mb-8 leading-relaxed">
                Kalkulator porównuje warianty przy założonych stawkach — a o realnych warunkach decyduje rynek. Jan Wojdyła bezpłatnie zbierze oferty z banków i firm leasingowych i przedstawi porównanie na Twoich liczbach.
              </p>
              <a
                href="tel:698084044"
                className="inline-flex items-center gap-3 px-8 py-4 bg-primary-500 text-white font-extrabold text-lg rounded-full hover:bg-primary-600 transition-all hover:-translate-y-0.5 shadow-lg shadow-primary-500/20 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl" />
                698 084 044
              </a>
              <div className="flex flex-wrap gap-5 justify-center mt-6">
                {[
                  'Oferty wielu instytucji',
                  'Wsparcie w dokumentach',
                  'Bez zobowiązań',
                ].map((t) => (
                  <span key={t} className="flex items-center gap-2 text-xs text-white/50">
                    <i className="ri-check-line text-green-400" />
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Legal */}
      <section className="pb-10 md:pb-14 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <p className="text-[11px] text-foreground-400 leading-relaxed text-center max-w-3xl mx-auto">
            Kalkulator ma charakter poglądowy i edukacyjny — nie stanowi porady podatkowej ani oferty. Skutki podatkowe zależą od indywidualnej sytuacji firmy; przed decyzją skonsultuj się z doradcą i księgowym.
          </p>
        </div>
      </section>
    </div>
  );
}